import { createNamespacedHelpers } from 'vuex'

import { addBookmark, deleteBookmark, editBookmark, editBookmarkList, pushBookmark } from './bookmarks'

const { mapState: mapBookmarkState } = createNamespacedHelpers('bookmark')
const { mapGetters: mapToolsGetters, mapState: mapToolsState } = createNamespacedHelpers('tools')

const BookmarkMixin = {
  computed: {
    ...mapBookmarkState({
      $confirmCancelBookmark: (state) => state.confirmCancelBookmark,
      $deleteIndexBookmark: (state) => state.deleteIndexBookmark,
      $deleteLabelBookmark: (state) => state.deleteLabelBookmark,
      $denyDeleteBookmark: (state) => state.denyDeleteBookmark,
      $formBookmark: (state) => state.formBookmark,
      $isModalVisibleBookmark: (state) => state.isModalVisibleBookmark,
      $isValidBookmarkForm: (state) => state.isValidBookmarkForm,
      $promptDeleteBookmark: (state) => state.promptDeleteBookmark,
    }),
    ...mapToolsGetters([
      'bookmarks',
      'currentNav',
      'routeMap',
      'selectedSearch',
    ]),
    ...mapToolsState({
      associationId: (state) => state.associationId,
      baseReportName: (state) => state.baseReportName,
      reportPath: (state) => state.reportPath,
      routeName: (state) => state.routeName,
      subId: (state) => state.subId,
    }),

    confirmCancelBookmark: {
      get() {
        return this.$confirmCancelBookmark
      },
      set(value) {
        this.$store.commit('bookmark/confirmCancelBookmark', value)
      },
    },
    promptDeleteBookmark: {
      get() {
        return this.$promptDeleteBookmark
      },
      set(value) {
        this.$store.commit('bookmark/promptDeleteBookmark', value)
      },
    },
    deleteIndexBookmark: {
      get() {
        return this.$deleteIndexBookmark
      },
      set(value) {
        this.$store.commit('bookmark/deleteIndexBookmark', value)
      },
    },
    deleteLabelBookmark: {
      get() {
        return this.$deleteLabelBookmark
      },
      set(value) {
        this.$store.commit('bookmark/deleteLabelBookmark', value)
      },
    },
    denyDeleteBookmark: {
      get() {
        return this.$denyDeleteBookmark
      },
      set(value) {
        this.$store.commit('bookmark/denyDeleteBookmark', value)
      },
    },
    isModalVisibleBookmark: {
      get() {
        return this.$isModalVisibleBookmark
      },
      set(newValue) {
        this.$store.commit('bookmark/isModalVisibleBookmark', newValue)
      },
    },
    isValidBookmarkForm: {
      get() {
        return this.$isValidBookmarkForm
      },
      set(value) {
        this.$store.commit('bookmark/isValidBookmarkForm', value)
      },
    },

    currentRouteBookmarkIndex() {
      const { associationId, baseReportName = null, routeName, reportPath, subId } = this
      return this.bookmarks.findIndex((bookmark) => {
        const matches = bookmark.routeName === routeName
          && bookmark.baseReportName === baseReportName
          && bookmark.associationId === associationId
          && bookmark.subId === subId
          && bookmark.reportPath === reportPath
        return matches
      })
    },
    isCurrentRouteBookmarked() {
      return this.currentRouteBookmarkIndex !== -1
    },
    bookmarkIcon() {
      return this.isCurrentRouteBookmarked ? 'bookmark' : 'bookmark_border'
    },

    formDataForBookmark: {
      get() {
        return this.$formBookmark
      },
      set(value) {
        this.$store.commit('bookmark/formBookmark', value)
      },
    },
    action: {
      get() {
        return this.formDataForBookmark.action
      },
      set(action) {
        const { entry, scope } = this.formDataForBookmark
        this.$store.commit('bookmark/formBookmark', { action, entry, scope }, { root: true })
      },
    },
    scope() {
      return this.formDataForBookmark.scope
    },
    entry: {
      get() {
        return this.formDataForBookmark.entry || []
      },
      set(entry) {
        const { action, scope } = this.formDataForBookmark
        this.$store.commit('bookmark/formBookmark', { action, entry, scope }, { root: true })
      },
    },

    entriesToEdit() {
      return this.entry?.filter((e) => e.isEditing) || []
    },
    isEditingMany() {
      return Array.isArray(this.entriesToEdit) && this.entriesToEdit.length > 1
    },

    isCreatingOrDeleting() {
      return ['create', 'delete'].indexOf(this.action) !== -1
    },
  },
  watch: {
    entry: {
      deep: true,
      handler() {},
    },
  },
  methods: {
    validate() {
      return this.$refs.refBookmarkForm.validate()
    },
    reset() {
      return this.$refs.refBookmarkForm.reset()
    },
    resetValidation() {
      return this.$refs.refBookmarkForm.resetValidation()
    },
    closeBookmarkForm() {
      if (!this.isCreatingOrDeleting) this.resetValidation()
      this.isModalVisibleBookmark = false
    },
    cancelForm() {
      this.entry = null
      this.closeBookmarkForm()
    },
    errorForm(message) {
      const status = 'error'

      this.$store.commit('tools/snackbar', true, { root: true })
      this.$store.commit('tools/snack', { message, status }, { root: true })
    },

    pushBookmark() {
      pushBookmark(this.entry)
    },

    editBookmarksList() {
      const action = 'edit'
      const scope = 'list'
      const entry = editBookmarkList(this.bookmarks)

      this.formDataForBookmark = { action, entry, scope }
      this.isModalVisibleBookmark = true
    },
    editBookmark(bookmarkToEdit) {
      const action = 'edit'
      const scope = 'item'
      const entry = editBookmark(bookmarkToEdit, this.bookmarks)

      this.formDataForBookmark = { action, entry, scope }
      this.isModalVisibleBookmark = true
    },

    submitBookmarkForm(successMessage) {
      const saveEdits = async () => {
        const { entry: entries } = this
        const bookmarks = entries.reduce((entriesArray, entry, entryIndex) => {
          entriesArray.push({
            associationId: entry.associationId,
            index: entryIndex,
            reportPath: entry.reportPath || null,
            baseReportName: entry.baseReportName || null,
            routeName: entry.routeName || this.routeMap[entry.baseReportName] || null,
            subId: entry.subId,
          })

          return entriesArray
        }, [])
        const changes = { bookmarks }
        const fnSuccess = () => {
          const message = successMessage
          const status = 'success'

          this.closeBookmarkForm()
          this.$store.commit('tools/snackbar', true, { root: true })
          this.$store.commit('tools/snack', { message, status }, { root: true })
        }

        return this.$store.dispatch('auth/handleUpdateToUserSettings', { changes, fnSuccess }, { root: true })
      }

      return this.isCreatingOrDeleting || this.validate() ? saveEdits() : this.errorForm('Please fill required fields correctly.')
    },

    cancelDeleteBookmark() {
      this.denyDeleteBookmark = false
      this.promptDeleteBookmark = false
    },
    askDeleteBookmark(bookmark) {
      this.deleteIndexBookmark = bookmark.index
      this.deleteLabelBookmark = bookmark.reportLabel
      this.promptDeleteBookmark = true
    },
    confirmDeleteBookmark(bookmark, bookmarks) {
      if (bookmarks.length === 1) {
        this.denyDeleteBookmark = true
      } else {
        this.askDeleteBookmark(bookmark)
      }
    },
    deleteBookmark() {
      const action = 'delete'
      const scope = 'item'
      const entry = deleteBookmark(this.deleteIndexBookmark, this.entry)

      this.formDataForBookmark = { action, entry, scope }
      this.submitBookmarkForm('Deleted')
      this.promptDeleteBookmark = false
    },
    removeBookmark(index) {
      if (this.isEditingItem) {
        this.confirmDeleteBookmark(this.entry[index], this.entry)
      } else {
        this.entry.splice(index, 1)
      }
    },

    bookmarkCurrentRoute() {
      const action = 'create'
      const scope = 'item'
      const { currentNav } = this
      const { baseReportName, routeName } = currentNav
      const { associationId, reportPath, subId } = this.$route.query
      const bookmarkToAdd = {
        associationId: associationId || null,
        index: this.bookmarks.length,
        isEditing: true,
        reportPath: reportPath || null,
        baseReportName: baseReportName || null,
        routeName: routeName || null,
        subId: subId || null,
      }
      const entry = addBookmark(bookmarkToAdd, this.bookmarks)

      this.formDataForBookmark = { action, entry, scope }
      this.submitBookmarkForm('Bookmarked')
    },
    deleteBookmarkCurrentRoute() {
      const { currentNav } = this
      const { baseReportName, routeName } = currentNav
      const { associationId, reportPath, subId } = this.$route.query
      const bookmarkToRemove = this.bookmarks.find((bookmark) => bookmark.routeName === routeName
        && bookmark.baseReportName === baseReportName
        && bookmark.reportPath === reportPath
        && bookmark.associationId === associationId
        && bookmark.subId === subId)

      if (bookmarkToRemove) {
        const action = 'delete'
        const scope = 'item'
        const entry = editBookmark(bookmarkToRemove, this.bookmarks)

        this.formDataForBookmark = { action, entry, scope }
        this.confirmDeleteBookmark(bookmarkToRemove, this.entry)
      } else {
        this.errorForm('Bookmark not found.')
      }
    },
    toggleBookmarkCurrentRoute() {
      if (this.isCurrentRouteBookmarked) {
        this.deleteBookmarkCurrentRoute()
      } else {
        this.bookmarkCurrentRoute()
      }
    },
  },
}

export default BookmarkMixin
